import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */

import { keysOf } from 'betterplace-utils';
import { get } from 'lodash';
import { useEffect, useId, useRef, useState } from 'react';
import type { Path, useForm } from 'react-hook-form';
import { z } from 'zod';
import { fromFormDataFlattened, parseFieldValue } from '@/form/utils';
export type FieldValueSetter<TSchema extends z.ZodType<any, any>> = ReturnType<typeof useForm<z.infer<TSchema>, any, undefined>>['setValue'];
export type UseHydrateFormWithUserDataReturn = {
  // id that needs to be passed to the form element
  id: string;
};
function useHydrateFormWithUserData(setValue, defaultValues, passThroughId) {
  const $ = _c(5);
  const defaultValuesRef = useRef(defaultValues);
  const setValueRef = useRef(setValue);
  const generatedId = useId();
  const id = passThroughId ?? generatedId;
  const [hydrationFormData, setHydrationFormData] = useState(null);
  if (!hydrationFormData && typeof document !== "undefined") {
    const form = document.getElementById(id) as HTMLFormElement | null;
    if (form) {
      const data = new FormData(form);
      const values = fromFormDataFlattened(data) ?? null;
      setHydrationFormData(values);
    }
  }
  let t0;
  let t1;
  if ($[0] !== hydrationFormData) {
    t0 = () => {
      const values_0 = hydrationFormData;
      if (!values_0) {
        return;
      }
      for (const key of keysOf(values_0)) {
        let value = values_0[key];
        const defaultValue = get(defaultValuesRef.current, key);
        if (typeof defaultValue === "boolean") {
          value = !!value && (value === "on" || value === "true") ? true : false;
        }
        if (typeof defaultValue === "number") {
          value = typeof value === "string" ? value.replace(/[.,]/g, ".") : value;
        }
        if (value === defaultValue) {
          continue;
        }
        value = parseFieldValue(value);
        if (value === defaultValue) {
          continue;
        }
        setValueRef.current(key, value, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    };
    t1 = [hydrationFormData];
    $[0] = hydrationFormData;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== id) {
    t2 = {
      id
    };
    $[3] = id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export default useHydrateFormWithUserData;